import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

const LoginDrawerContext = createContext(null);

export function LoginDrawerProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const [reason, setReason] = useState(null);
  const [authIntent, setAuthIntent] = useState(null);
  const [shouldPersist, setShouldPersist] = useState(false);
  const closeOnRouteChangeRef = useRef(true);

  const open = useCallback((nextReason = null, { persist = false, authIntent: nextAuthIntent = null } = {}) => {
    setIsOpen(true);
    setReason(nextReason);
    setAuthIntent(nextAuthIntent);
    setShouldPersist(Boolean(persist));
    if (persist) {
      closeOnRouteChangeRef.current = false;
    }
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setReason(null);
    setAuthIntent(null);
    setShouldPersist(false);
    closeOnRouteChangeRef.current = true;
  }, []);

  const toggle = useCallback(
    (nextReason = null, options) => {
      if (isOpen) {
        close();
      } else {
        open(nextReason, options);
      }
    },
    [close, isOpen, open]
  );

  const registerRouteChange = useCallback(() => {
    if (!shouldPersist && closeOnRouteChangeRef.current) {
      close();
    }
  }, [close, shouldPersist]);

  const value = useMemo(
    () => ({
      isOpen,
      reason,
      authIntent,
      open,
      close,
      toggle,
      registerRouteChange,
    }),
    [authIntent, isOpen, reason, open, close, toggle, registerRouteChange]
  );

  return <LoginDrawerContext.Provider value={value}>{children}</LoginDrawerContext.Provider>;
}

export const useLoginDrawer = () => {
  const context = useContext(LoginDrawerContext);
  if (!context) {
    throw new Error('useLoginDrawer must be used within a LoginDrawerProvider');
  }
  return context;
};
